import { forwardRef, memo, useMemo } from "react";
import { cn } from "@/lib/utils";

import { V2Position } from "@/hooks/useFloorPlanV2";
import { Table, Reservation } from "@/types/table";
import { TableStatus, getTableServiceMeta } from "@/lib/floorPlanUtils";
import { getSlotColor } from "@/lib/slotColors";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Users, Lock, Clock, ArrowRight } from "lucide-react";

interface Props {
  position: V2Position;
  table: Table;
  isSelected: boolean;
  hasOverlap: boolean;
  zoneColor?: string;
  showSeats?: boolean;
  // 'edit' = drag/select, 'service' = click only, 'view' = read-only
  interactionMode?: 'edit' | 'service' | 'view';
  // Service mode
  status?: TableStatus;
  reservation?: Reservation;
  /** All reservations covering this table on the selected day (sorted asc by time). */
  dayReservations?: Reservation[];
  // Cross-highlight from reservation panel
  isHighlighted?: boolean;
  // Drop-target state (service mode, drag-and-drop from panel)
  dropState?: 'valid' | 'invalid' | 'hover-valid' | 'hover-invalid' | 'swap' | 'hover-swap' | null;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  /** Étape 7 — manual compact density: hide capacity row + next-res row, keep name + status dot. */
  compact?: boolean;
}

const STATUS_BG: Record<TableStatus, string> = {
  free: 'bg-green-50 dark:bg-green-950/30 border-green-300 dark:border-green-800',
  reserved: 'bg-blue-50 dark:bg-blue-950/40 border-blue-500/70',
  arrived: 'bg-emerald-600 dark:bg-emerald-700 border-emerald-700 dark:border-emerald-500 text-white',
  late: 'bg-orange-100 dark:bg-orange-950/50 border-orange-500',
  'no-show': 'bg-red-100 dark:bg-red-950/50 border-red-600 text-red-900 dark:text-red-200',
  cancelled: 'bg-muted border-muted-foreground/30 opacity-60',
  inactive: 'bg-muted border-muted-foreground/20 opacity-50',
};

const STATUS_DOT: Record<TableStatus, string> = {
  free: 'bg-emerald-500',
  reserved: 'bg-blue-500',
  arrived: 'bg-emerald-600',
  late: 'bg-orange-500',
  'no-show': 'bg-red-600',
  cancelled: 'bg-muted-foreground',
  inactive: 'bg-muted-foreground/40',
};

const Seats = ({ position, count }: { position: V2Position; count: number }) => {
  const seats = useMemo(() => {
    const arr: { left: number; top: number }[] = [];
    if (count <= 0) return arr;
    const w = position.width, h = position.height;
    const isRound = position.shape === 'round' || position.shape === 'oval';
    if (isRound) {
      const rx = w / 2 + 8;
      const ry = h / 2 + 8;
      for (let i = 0; i < count; i++) {
        const a = (i / count) * Math.PI * 2 - Math.PI / 2;
        arr.push({ left: w / 2 + rx * Math.cos(a) - 5, top: h / 2 + ry * Math.sin(a) - 5 });
      }
    } else {
      // distribute around perimeter (rect)
      const perH = Math.max(1, Math.ceil(count / 4));
      const perV = Math.max(1, Math.floor((count - perH * 2) / 2));
      let placed = 0;
      // top
      for (let i = 0; i < perH && placed < count; i++, placed++) {
        arr.push({ left: ((i + 1) * w) / (perH + 1) - 5, top: -12 });
      }
      // bottom
      for (let i = 0; i < perH && placed < count; i++, placed++) {
        arr.push({ left: ((i + 1) * w) / (perH + 1) - 5, top: h + 2 });
      }
      // left
      for (let i = 0; i < perV && placed < count; i++, placed++) {
        arr.push({ left: -12, top: ((i + 1) * h) / (perV + 1) - 5 });
      }
      // right
      for (let i = 0; i < perV && placed < count; i++, placed++) {
        arr.push({ left: w + 2, top: ((i + 1) * h) / (perV + 1) - 5 });
      }
    }
    return arr;
  }, [count, position.width, position.height, position.shape]);

  return (
    <>
      {seats.map((s, i) => (
        <span
          key={i}
          className="absolute w-[10px] h-[10px] rounded-full bg-muted-foreground/40 border border-background"
          style={{ left: s.left, top: s.top }}
        />
      ))}
    </>
  );
};

export const V2Table = memo(forwardRef<HTMLDivElement, Props>(({
  position, table, isSelected, hasOverlap, zoneColor,
  showSeats, interactionMode = 'edit', status, reservation,
  dayReservations, isHighlighted, dropState, onDragOver, onDragLeave, onDrop,
  compact = false,
}: Props, ref) => {
  const shape = position.shape || 'rect';
  const isRound = shape === 'round' || shape === 'oval';
  const isBar = shape === 'bar';

  const customerInitials = useMemo(() => {
    if (!reservation) return null;
    const parts = [reservation.customerFirstName, reservation.customerLastName]
      .filter(Boolean).join(' ').trim();
    if (!parts) return null;
    return parts.split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  }, [reservation]);

  const minDim = Math.min(position.width, position.height);
  const showCapacityRow = minDim >= 44;

  const cursor = (() => {
    if (interactionMode === 'view') return 'default';
    if (interactionMode === 'edit') {
      return isSelected ? 'move' : 'grab';
    }
    // service
    if (dropState === 'hover-valid' || dropState === 'valid') return 'copy';
    if (dropState === 'hover-swap' || dropState === 'swap') return 'alias';
    if (dropState === 'invalid' || dropState === 'hover-invalid') return 'not-allowed';
    if (status === 'inactive' || status === 'cancelled') return 'default';
    return 'pointer';
  })();

  // B1/B2 — only relevant in service mode
  const activeDayRes = useMemo(
    () => (dayReservations ?? []).filter(r => r.status !== 'annulé'),
    [dayReservations]
  );
  const isDoubleService = interactionMode === 'service' && activeDayRes.length >= 2;
  // Bicolor split using two distinct slot colors (first + last reservation of the day)
  const splitColors = useMemo(() => {
    if (!isDoubleService) return null;
    const first = activeDayRes[0].startTime.substring(0, 5);
    const last = activeDayRes[activeDayRes.length - 1].startTime.substring(0, 5);
    return { a: getSlotColor(first), b: getSlotColor(last) };
  }, [isDoubleService, activeDayRes]);

  // Étape 6 — service meta: chronomètre + next reservation
  const serviceMeta = useMemo(() => {
    if (interactionMode !== 'service') return null;
    return getTableServiceMeta(reservation, dayReservations, new Date());
  }, [interactionMode, reservation, dayReservations]);

  const tableNode = (
    <div
      ref={ref}
      data-v2-table-id={position.tableId}
      data-v2-status={status || ''}
      className={cn(
        "absolute flex flex-col items-center justify-center select-none border-2 transition-all",
        isRound ? "rounded-full" : (isBar ? "rounded-sm" : "rounded-md"),
        status ? STATUS_BG[status] : "bg-card text-foreground border-muted-foreground/30",
        // Étape 6 — animations de statut
        status === 'late' && "animate-pulse-slow",
        status === 'no-show' && "animate-blink-strong",
        isSelected && "ring-2 ring-primary shadow-md",
        hasOverlap && "ring-2 ring-destructive border-destructive/70",
        interactionMode === 'service' && status !== 'inactive' && status !== 'cancelled' && "hover:ring-2 hover:ring-primary/50 hover:shadow-md",
        isHighlighted && "ring-4 ring-amber-400 shadow-[0_0_0_2px_rgba(251,191,36,0.4)] z-10",
        dropState === 'valid' && "ring-2 ring-emerald-500/70 ring-offset-1",
        dropState === 'hover-valid' && "ring-4 ring-emerald-500 shadow-lg scale-105 z-20",
        dropState === 'swap' && "ring-2 ring-amber-500/70 ring-offset-1",
        dropState === 'hover-swap' && "ring-4 ring-amber-500 shadow-lg scale-105 z-20",
        dropState === 'invalid' && "opacity-40 grayscale",
        dropState === 'hover-invalid' && "ring-4 ring-destructive opacity-60 z-20",
      )}
      style={{
        left: position.x,
        top: position.y,
        width: position.width,
        height: position.height,
        transform: position.rotation ? `rotate(${position.rotation}deg)` : undefined,
        transformOrigin: 'center center',
        borderLeftColor: zoneColor || undefined,
        borderLeftWidth: zoneColor ? 4 : undefined,
        cursor,
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {showSeats && <Seats position={position} count={table.capacity} />}

      {status && status !== 'free' && status !== 'inactive' && (
        <span
          className={cn(
            "absolute top-1 left-1 w-2 h-2 rounded-full",
            STATUS_DOT[status],
            status === 'late' && "animate-ping"
          )}
        />
      )}
      {status === 'late' && (
        <span className="absolute top-1 left-1 w-2 h-2 rounded-full bg-orange-500" />
      )}

      {/* C2 — Lock indicator on table (current slot reservation) */}
      {reservation?.isLocked && (
        <span
          aria-label="Réservation verrouillée"
          className="absolute bottom-0.5 left-0.5 flex items-center justify-center w-3.5 h-3.5 rounded-sm bg-background/85 border border-foreground/30 shadow-sm pointer-events-none"
        >
          <Lock className="w-2 h-2 text-foreground" />
        </span>
      )}

      {/* B1 — Double-service marker: bicolor split chevron in top-right corner */}
      {splitColors && (
        <span
          aria-label={`${activeDayRes.length} services aujourd'hui`}
          className="absolute top-0.5 right-0.5 w-3.5 h-3.5 rounded-sm shadow-sm ring-1 ring-background/60 overflow-hidden pointer-events-none"
          style={{
            background: `linear-gradient(135deg, ${splitColors.a} 0 50%, ${splitColors.b} 50% 100%)`,
          }}
        />
      )}

      {/* Arrived: show client initials big */}
      {status === 'arrived' && customerInitials ? (
        <div className="font-bold text-white" style={{ fontSize: `clamp(14px, ${minDim * 0.32}px, 22px)` }}>
          {customerInitials}
        </div>
      ) : (
        <div
          className="font-semibold leading-tight text-center px-1 truncate w-full"
          style={{ fontSize: `clamp(10px, ${minDim * 0.22}px, 14px)` }}
        >
          {table.name}
        </div>
      )}

      {showCapacityRow && !compact && (
        <div
          className="text-muted-foreground flex items-center gap-1"
          style={{ fontSize: `clamp(8px, ${minDim * 0.14}px, 11px)` }}
        >
          {reservation ? (
            <span className="font-medium text-foreground">
              {reservation.numberOfPeople}p · {reservation.startTime.substring(0, 5)}
            </span>
          ) : (
            <span>{table.capacity}p</span>
          )}
        </div>
      )}

      {/* Étape 6 — chronomètre "libre HH:MM" pour les tables occupées */}
      {showCapacityRow && !compact && interactionMode === 'service' && status === 'arrived' && serviceMeta?.freeAt && (
        <div
          className={cn(
            "flex items-center gap-0.5 leading-none mt-0.5",
            status === 'arrived' ? "text-white/90" : "text-muted-foreground"
          )}
          style={{ fontSize: `clamp(8px, ${minDim * 0.13}px, 10px)` }}
        >
          <Clock className="w-2.5 h-2.5" />
          <span>libre {serviceMeta.freeAt}</span>
        </div>
      )}

      {/* Étape 6 — prochaine réservation à venir (table libre/réservée) */}
      {showCapacityRow && !compact && interactionMode === 'service' && !reservation && serviceMeta?.nextReservation && (
        <div
          className="flex items-center gap-0.5 leading-none mt-0.5 text-muted-foreground"
          style={{ fontSize: `clamp(8px, ${minDim * 0.13}px, 10px)` }}
        >
          <ArrowRight className="w-2.5 h-2.5" />
          <span className="truncate max-w-[80%]">
            {serviceMeta.nextReservation.startTime.substring(0, 5)} {serviceMeta.nextReservation.customerLastName || serviceMeta.nextReservation.customerFirstName || ''} {serviceMeta.nextReservation.numberOfPeople}p
          </span>
        </div>
      )}
    </div>
  );

  // B2 — Day timeline tooltip (service mode + at least one reservation)
  if (interactionMode === 'service' && activeDayRes.length > 0) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{tableNode}</TooltipTrigger>
        <TooltipContent side="top" align="center" className="p-0 max-w-[260px]">
          <div className="px-2.5 py-1.5 border-b bg-muted/40 text-[11px] font-semibold flex items-center justify-between gap-2">
            <span>{table.name}</span>
            <span className="text-muted-foreground font-normal">
              {activeDayRes.length} {activeDayRes.length > 1 ? 'services' : 'service'}
            </span>
          </div>
          <ul className="py-1">
            {activeDayRes.map(r => (
              <li
                key={r.id}
                className="flex items-center gap-2 px-2.5 py-1 text-[11px]"
              >
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ background: getSlotColor(r.startTime.substring(0, 5)) }}
                />
                <span className="font-mono w-10 shrink-0">{r.startTime.substring(0, 5)}</span>
                <span className="truncate flex-1">
                  {r.customerFirstName} {r.customerLastName}
                </span>
                <span className="flex items-center gap-0.5 text-muted-foreground shrink-0">
                  <Users className="w-2.5 h-2.5" />
                  {r.numberOfPeople}
                </span>
                {r.status === 'arrivé' && (
                  <span className="text-emerald-600 dark:text-emerald-400 text-[10px]">✓</span>
                )}
              </li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    );
  }

  return tableNode;
}));
V2Table.displayName = 'V2Table';

